/**
 * useRemoteDataV2 — Schema-driven remote list data fetching for DynamicTableV2.
 *
 * When schema.dataSource is present and consumer does NOT pass `data`,
 * this hook fetches list data automatically using the schema endpoint/params,
 * maps the response, and feeds it into table state.
 *
 * Supports three pagination modes:
 * - "pagination" / "clientSide": Standard page-based fetching (replaces data on each fetch)
 * - "loadMore": Accumulates data across pages, exposes loadMore() function
 * - "infiniteScroll": Same as loadMore but triggered by scroll events
 *
 * Optional **continuationCursor** on `dataSource` (see `ContinuationCursorConfig` in schema types):
 * on accumulation page ≥ 1, appends a query param from the last accumulated row's field
 * (e.g. `DateTime` ← `createdDate` for trade-order dashboard APIs).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { InternalDataSourceConfig } from '../core/schema/types';
import { getToken } from '../utils/getToken';
import {
  buildQueryParams,
  formatContinuationCursorValue,
  getNestedValue,
  serializeQueryParams,
} from '../utils/remoteDataHelpers';

// Only the fields that actually affect what data to fetch.
// Deliberately excludes: data, selectedKeys, editingRows, expandedRows, etc.
// so that setData() after a successful fetch does NOT trigger another fetch.
export interface FetchParams {
  searchText: string;
  filters: Record<string, unknown>;
  sort: { field: string; direction: 'asc' | 'desc' } | null;
  pageIndex: number;
  pageSize: number;
}

export type PaginationMode = 'pagination' | 'loadMore' | 'infiniteScroll' | 'clientSide' | 'none';

export interface RemoteDataConfig {
  dataSource: Readonly<InternalDataSourceConfig> | null;
  fetchParams: FetchParams;
  axiosInstance?: { get: Function; post: Function; request: Function };
  refreshGeneration: number;
  externalDataProvided: boolean;
  /** Pagination mode - determines whether to accumulate data (loadMore/infiniteScroll) or replace (pagination/clientSide) */
  paginationMode?: PaginationMode;
}

export interface RemoteDataResult {
  data: unknown[];
  totalCount: number;
  loading: 'idle' | 'loading' | 'success' | 'error';
  error: string | null;
  refetch: () => void;
  /** For loadMore/infiniteScroll: function to load the next page */
  loadMore: () => void;
  /** For loadMore/infiniteScroll: whether more data is available */
  hasMore: boolean;
  /** True when fetching page > 0 in accumulation mode (not the initial load) */
  isLoadingMore: boolean;
  /** For loadMore/infiniteScroll: current page index (0-based) */
  currentPage: number;
  /** True when `fetchPolicy: 'requireFilter'` is active and no search/filter is applied */
  isAwaitingFilter: boolean;
}

function isActiveFilterValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export function hasActiveQuery(p: FetchParams): boolean {
  if (p.searchText.trim()) return true;
  return Object.values(p.filters).some(isActiveFilterValue);
}

export function useRemoteDataV2(config: RemoteDataConfig): RemoteDataResult {
  const {
    dataSource,
    fetchParams,
    axiosInstance,
    refreshGeneration,
    externalDataProvided,
    paginationMode = 'pagination',
  } = config;

  const isAccumulationMode = paginationMode === 'loadMore' || paginationMode === 'infiniteScroll';

  // For standard pagination: single page of data
  // For loadMore/infiniteScroll: accumulated data across all loaded pages
  const [data, setData] = useState<unknown[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // For loadMore/infiniteScroll: track current page independently of fetchParams.pageIndex
  const [accumulationPage, setAccumulationPage] = useState(0);
  /** Sync page index for fetches — reset synchronously when query signature changes (before fetch effect). */
  const accumulationPageRef = useRef(0);

  // For cursor-based APIs (hasMoreItems flag): null means use totalCount-based calculation
  const [serverHasMore, setServerHasMore] = useState<boolean | null>(null);

  /** Latest accumulated rows — used to read last row for continuationCursor before the next fetch. */
  const dataRef = useRef<unknown[]>([]);

  const mountedRef = useRef(true);
  const fetchCountRef = useRef(0);

  // Track the last filter/search/sort signature to detect when we need to reset accumulation
  const lastQuerySignatureRef = useRef<string>('');

  // Keep a ref to fetchParams so the imperative refetch() always uses the
  // latest values without being a dep that recreates fetchData.
  const fetchParamsRef = useRef(fetchParams);
  fetchParamsRef.current = fetchParams;

  const dataSourceRef = useRef(dataSource);
  dataSourceRef.current = dataSource;

  const axiosRef = useRef(axiosInstance);
  axiosRef.current = axiosInstance;

  const paginationModeRef = useRef(paginationMode);
  paginationModeRef.current = paginationMode;

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Generate a signature for filter/search/sort to detect changes
  const getQuerySignature = useCallback((p: FetchParams): string => {
    return JSON.stringify({
      searchText: p.searchText,
      filters: p.filters,
      sort: p.sort,
    });
  }, []);

  // Reset accumulation when filters/search/sort change
  useEffect(() => {
    if (!isAccumulationMode) return;

    const currentSignature = getQuerySignature(fetchParams);
    if (
      lastQuerySignatureRef.current !== '' &&
      lastQuerySignatureRef.current !== currentSignature
    ) {
      accumulationPageRef.current = 0;
      setAccumulationPage(0);
      dataRef.current = [];
      setData([]);
      setServerHasMore(null);
    }
    lastQuerySignatureRef.current = currentSignature;
  }, [fetchParams, isAccumulationMode, getQuerySignature]);

  // Also reset on refreshGeneration change (manual refresh)
  const lastRefreshRef = useRef(refreshGeneration);
  useEffect(() => {
    if (isAccumulationMode && lastRefreshRef.current !== refreshGeneration) {
      lastRefreshRef.current = refreshGeneration;
      accumulationPageRef.current = 0;
      setAccumulationPage(0);
      dataRef.current = [];
      setData([]);
      setServerHasMore(null);
    }
  }, [refreshGeneration, isAccumulationMode]);

  const fetchData = useCallback(
    async (pageOverride?: number) => {
      const ds = dataSourceRef.current;
      if (!ds || externalDataProvided) return;

      if (ds.fetchPolicy === 'requireFilter' && !hasActiveQuery(fetchParamsRef.current)) {
        setData([]);
        setTotalCount(0);
        setLoading('idle');
        return;
      }

      const currentFetch = ++fetchCountRef.current;
      if (dataRef.current.length === 0) {
        setLoading('loading');
      }
      setError(null);

      const p = fetchParamsRef.current;
      const ax = axiosRef.current;
      const mode = paginationModeRef.current;
      const isAccumulating = mode === 'loadMore' || mode === 'infiniteScroll';

      // For accumulation mode, use ref so filter/search changes always fetch page 0 (state updates async).
      const effectivePageIndex = isAccumulating
        ? pageOverride ?? accumulationPageRef.current
        : p.pageIndex;

      // Build params with the effective page index
      const effectiveParams: FetchParams = {
        ...p,
        pageIndex: effectivePageIndex,
      };

      try {
        let queryParams = buildQueryParams(ds, effectiveParams);

        if (
          isAccumulating &&
          effectivePageIndex > 0 &&
          ds.continuationCursor != null
        ) {
          const cfg = ds.continuationCursor;
          const acc = dataRef.current;
          const last =
            Array.isArray(acc) && acc.length > 0
              ? (acc[acc.length - 1] as Record<string, unknown>)
              : null;
          if (last != null && typeof last === 'object') {
            const raw = getNestedValue(last, cfg.fieldPath);
            const formatted = formatContinuationCursorValue(raw, cfg.format ?? 'preserve');
            if (formatted !== undefined && formatted !== '') {
              queryParams = { ...queryParams, [cfg.queryKey]: formatted };
            }
          }
        }

        // Schema endpoints are relative paths (e.g. "/api/customer/api/app/participant/participants-list").
        // axiosInstance already has baseURL configured — just pass the path as-is.
        const path = ds.endpoint.replace(/^\//, '');

        let response: any;

        if (ax) {
          // axiosInstance handles baseURL, Authorization, X-Tenant-Id, etc. via interceptors.
          //
          // IMPORTANT:
          // We deliberately do NOT abort in-flight axios requests on rapid filter changes.
          // Some app-level interceptors treat cancellations as fatal and can redirect the
          // whole SPA (maintenance page). Instead we allow overlap and ignore stale
          // responses using currentFetch / fetchCountRef.
          if (ds.method === 'POST') {
            response = await ax.post(path, queryParams);
          } else {
            response = await ax.get(path, { params: queryParams, paramsSerializer: serializeQueryParams });
          }
        } else {
          // Fallback: no axiosInstance — use native fetch with manual token + full URL
          const token = getToken();
          const baseUrl = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
          const fullUrl = ds.endpoint.startsWith('http') ? ds.endpoint : `${baseUrl}/${path}`;
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (token) headers.Authorization = `Bearer ${token}`;

          const fetchUrl =
            ds.method === 'GET'
              ? `${fullUrl}?${serializeQueryParams(queryParams)}`
              : fullUrl;

          const fetchOptions: RequestInit = {
            method: ds.method,
            headers,
          };
          if (ds.method === 'POST') {
            fetchOptions.body = JSON.stringify(queryParams);
          }
          const raw = await fetch(fetchUrl, fetchOptions);
          if (!raw.ok) throw new Error(`Request failed: ${raw.status}`);
          response = { data: await raw.json() };
        }

        if (currentFetch !== fetchCountRef.current) return;
        if (!mountedRef.current) return;

        const responseData = response.data;

        // Support three response shapes:
        //   1. Standard paginated: { items: [...], totalCount: N }
        //   2. Cursor-based:       { items: [...], hasMoreItems: true/false }
        //   3. Plain array:        [...]
        // The table auto-detects which format the API uses.
        let newItems: unknown[];
        let newTotalCount: number;
        let apiHasMore: boolean | undefined;

        if (Array.isArray(responseData)) {
          newItems = responseData;
          newTotalCount = responseData.length;
        } else {
          const items = getNestedValue(responseData, ds.responseMapping.items);
          const count = getNestedValue(responseData, ds.responseMapping.totalCount);
          const hasMoreFlag = getNestedValue(responseData, ds.responseMapping.hasMoreItems);

          newItems = Array.isArray(items) ? items : [];
          newTotalCount = typeof count === 'number' ? count : newItems.length;

          if (typeof hasMoreFlag === 'boolean') {
            apiHasMore = hasMoreFlag;
          }
        }

        if (isAccumulating) {
          if (effectivePageIndex === 0) {
            setData(newItems);
          } else {
            setData((prev) => [...prev, ...newItems]);
          }
          // For cursor-based APIs: derive hasMore from the flag.
          // For totalCount-based APIs: derive hasMore from count comparison (done in the return).
          if (apiHasMore !== undefined) {
            setServerHasMore(apiHasMore);
          } else {
            setServerHasMore(null);
          }
        } else {
          setData(newItems);
        }

        setTotalCount(newTotalCount);
        setLoading('success');
      } catch (err: unknown) {
        if (currentFetch !== fetchCountRef.current) return;
        if (!mountedRef.current) return;

        const errName =
          err && typeof err === 'object' && 'name' in err
            ? String((err as { name: unknown }).name)
            : '';
        if (errName === 'AbortError' || errName === 'CanceledError') return;

        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to fetch data';

        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[DynamicTableV2] Remote fetch failed:', err);
        }
        setError(message);
        setLoading('error');
      }
      // Only re-fetch when the query parameters that affect the API response change.
      // Deliberately NOT including fetchParams object — we read from ref to avoid
      // stale closures while keeping the dependency list stable.
      // For accumulation mode, we also depend on accumulationPage.
    },
    [
      externalDataProvided,
      fetchParams.searchText,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(fetchParams.filters),
      fetchParams.sort?.field,
      fetchParams.sort?.direction,
      fetchParams.pageSize,
      fetchParams.pageIndex,
      refreshGeneration,
      isAccumulationMode,
    ]
  );

  useEffect(() => {
    mountedRef.current = true;
    void fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  // Load more function for loadMore/infiniteScroll modes
  const loadMore = useCallback(() => {
    if (!isAccumulationMode) return;
    if (loading === 'loading') return;

    const nextPage = accumulationPageRef.current + 1;
    accumulationPageRef.current = nextPage;
    setAccumulationPage(nextPage);
    void fetchData(nextPage);
  }, [isAccumulationMode, loading, fetchData]);

  const refetch = useCallback(() => {
    if (isAccumulationMode) {
      accumulationPageRef.current = 0;
      setAccumulationPage(0);
      dataRef.current = [];
      setData([]);
      setServerHasMore(null);
    }
    void fetchData(0);
  }, [fetchData, isAccumulationMode]);

  const hasMore = isAccumulationMode
    ? serverHasMore !== null
      ? serverHasMore
      : data.length < totalCount
    : false;

  const isLoadingMore = isAccumulationMode && accumulationPage > 0 && loading === 'loading';

  const isAwaitingFilter =
    dataSource?.fetchPolicy === 'requireFilter' &&
    !hasActiveQuery(fetchParams) &&
    loading === 'idle';

  return {
    data,
    totalCount,
    loading,
    error,
    refetch,
    loadMore,
    hasMore,
    isLoadingMore,
    currentPage: isAccumulationMode ? accumulationPage : fetchParams.pageIndex,
    isAwaitingFilter,
  };
}
